import React, { useEffect, useRef, useState } from 'react';

import { WrappedControlProps } from 'app/components/Controls/WrappedControlProps';
import { useDidUpdateEffect } from 'app/shared/hooks/useDidUpdateEffect';
import { UtilService } from 'app/services/util.service';
import { isValidIban } from 'app/validators/validateIban';

export function useIbanControl({
  input,
  meta: { touched, valid, pristine },
  isQuestionValid,
  onValueChange,
  onControlBlur,
}: WrappedControlProps) {
  const fieldCleared = useRef(false);
  const ownUpdate = useRef(false);
  const [localValue, setLocalValue] = useState<string>('');
  const [focused, setFocused] = useState(false);
  const onFocus = () => setFocused(true);
  const error = touched && !valid;
  const mask = '99 9999 9999 9999 9999 9999 9999';
  const maskChar = null;
  const isIbanValid = localValue === '' || isValidIban(localValue);
  const isValueCheckVisible =
    isQuestionValid && !focused && UtilService.hasValue(input.value);

  const onPropsValueChange = () => {
    if (
      !input.value ||
      input.value.replace(/\s/g, '') === localValue.replace(/\s/g, '')
    )
      return;

    if (ownUpdate.current) {
      ownUpdate.current = false;
      return;
    }

    if (!fieldCleared.current) {
      setLocalValue(input.value);
      fieldCleared.current = false;
    }
  };

  const onLocalValueChange = () => {
    if (isIbanValid) {
      notifyChange();
    } else if (!pristine) {
      ownUpdate.current = true;
      input.onBlur(localValue);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(event.target.value);
  };

  const handleBlur = () => {
    pristine = false;
    setFocused(false);
    if (isIbanValid) {
      input.onBlur(localValue);
      const shouldPatch = isValidIban(localValue) || !localValue;
      if (onControlBlur) {
        onControlBlur(shouldPatch);
      }
    } else {
      input.onChange(localValue);
      setTimeout(() => {
        input.onBlur(localValue);
      }, 0);
    }
  };

  const notifyChange = () => {
    input.onChange(localValue);
    if (localValue === '') fieldCleared.current = true;

    if (onValueChange) {
      onValueChange(localValue, true);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(onPropsValueChange, [input.value]);
  useDidUpdateEffect(onLocalValueChange, [localValue]);

  return {
    error,
    focused,
    isValueCheckVisible,
    localValue,
    mask,
    maskChar,
    handleBlur,
    handleChange,
    onFocus,
  };
}
